/**
 * ConsentsPanel — consentimientos de crédito (APC / buró de crédito) de un
 * expediente. Lista los consentimientos, permite registrar uno nuevo y revocar
 * los que están vigentes. es-PA. Presentational + TanStack Query.
 *
 * Owned file (panel). Imports only existing design-system primitives and the
 * shared `api` client. Wired into the detail page during the integration phase.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import type { Consent, CreateConsentInput, Persona } from '../lib/phase1';
import { Button, Card, Dialog, EmptyState, Input, Label, Pill, Skeleton, Tooltip, type Tone } from './ui';
import { ReasonModal } from './compliance';
import { ShieldCheck, Plus, FileText, RotateCcw } from 'lucide-react';

// Shared control class with focus/placeholder styling, matching ui.tsx's Input
// and the SELECT_CLS used by ReviewsPanel/OwnersPanel.
const CONTROL_CLS =
  'h-10 w-full rounded-lg border border-line-strong bg-canvas px-3 text-sm text-ink placeholder:text-ink-faint focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30';

const PERSONA_LABEL: Record<Persona, string> = {
  natural: 'Persona natural',
  legal: 'Persona jurídica',
};

/** Estado derivado de un consentimiento, para pill + texto. */
type ConsentStatus = { label: string; tone: Tone };

function consentStatus(c: Consent): ConsentStatus {
  if (c.revokedAt) return { label: 'Revocado', tone: 'red' };
  if (c.expiresAt && new Date(c.expiresAt).getTime() < Date.now())
    return { label: 'Vencido', tone: 'amber' };
  return { label: 'Vigente', tone: 'green' };
}

const REVOKE_REASONS = [
  'Cliente retiró el consentimiento',
  'Consentimiento otorgado por error',
  'Datos del cliente actualizados',
  'Fin de la relación comercial',
];

interface NewConsentForm {
  subjectRef: string;
  subjectType: Persona;
  source: string;
  evidenceRef: string;
  grantedAt: string;
  expiresAt: string;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyForm = (): NewConsentForm => ({
  subjectRef: '',
  subjectType: 'natural',
  source: 'APC',
  evidenceRef: '',
  grantedAt: todayIso(),
  expiresAt: '',
});

export default function ConsentsPanel({ expedienteId }: { expedienteId: string }) {
  const qc = useQueryClient();
  const toast = useToast();

  const { data: consents, isLoading, isError, refetch } = useQuery({
    queryKey: ['consents', expedienteId],
    queryFn: () => api.listConsents(expedienteId),
  });

  const [recordOpen, setRecordOpen] = useState(false);
  const [form, setForm] = useState<NewConsentForm>(emptyForm);
  const [revokeTarget, setRevokeTarget] = useState<Consent | null>(null);

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['consents', expedienteId] });

  const create = useMutation({
    mutationFn: (body: CreateConsentInput) => api.createConsent(expedienteId, body),
    onSuccess: () => {
      void invalidate();
      toast({ message: 'Consentimiento registrado', kind: 'success' });
      setRecordOpen(false);
      setForm(emptyForm());
    },
    onError: () =>
      toast({ message: 'No se pudo registrar el consentimiento', kind: 'error' }),
  });

  const revoke = useMutation({
    mutationFn: (id: string) =>
      api.updateConsent(expedienteId, id, { revokedAt: new Date().toISOString() }),
    onSuccess: () => {
      void invalidate();
      toast({ message: 'Consentimiento revocado', kind: 'success' });
      setRevokeTarget(null);
    },
    onError: () =>
      toast({ message: 'No se pudo revocar el consentimiento', kind: 'error' }),
  });

  function submitRecord() {
    const subjectRef = form.subjectRef.trim();
    const source = form.source.trim();
    if (!subjectRef || !source) return;
    const body: CreateConsentInput = {
      subjectRef,
      subjectType: form.subjectType,
      source,
      ...(form.evidenceRef.trim() ? { evidenceRef: form.evidenceRef.trim() } : {}),
      ...(form.grantedAt ? { grantedAt: new Date(form.grantedAt).toISOString() } : {}),
      expiresAt: form.expiresAt ? new Date(form.expiresAt).toISOString() : null,
    };
    create.mutate(body);
  }

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl font-medium text-ink">Consentimientos de crédito</h2>
          <p className="mt-1 text-sm text-ink-soft">
            Autorizaciones de consulta a{' '}
            <Tooltip content="Asociación Panameña de Crédito — buró de información crediticia habilitado por la SBP.">
              <span className="underline decoration-dotted">buró de crédito (APC)</span>
            </Tooltip>
            {' '}registradas para este expediente.
          </p>
        </div>
        <Button variant="outline" onClick={() => setRecordOpen(true)} disabled={isLoading}>
          <Plus className="h-4 w-4" strokeWidth={2.5} /> Registrar
        </Button>
      </div>

      {isLoading ? (
        <div className="grid gap-2.5">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : isError ? (
        <EmptyState
          icon={<ShieldCheck className="h-8 w-8" strokeWidth={1.5} />}
          title="No se pudieron cargar los consentimientos"
          hint="Revisa tu conexión e inténtalo de nuevo."
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              <RotateCcw className="h-4 w-4" strokeWidth={2} /> Reintentar
            </Button>
          }
        />
      ) : !consents || consents.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck className="h-8 w-8" strokeWidth={1.5} />}
          title="Sin consentimientos registrados"
          hint="Cuando el cliente autorice la consulta a buró de crédito, regístralo aquí para dejar evidencia en la bitácora."
          action={
            <Button variant="outline" onClick={() => setRecordOpen(true)}>
              <Plus className="h-4 w-4" strokeWidth={2.5} /> Registrar consentimiento
            </Button>
          }
        />
      ) : (
        <div className="grid gap-2.5 [&>*]:min-w-0">
          {consents.map((c) => {
            const status = consentStatus(c);
            const active = !c.revokedAt;
            return (
              <div
                key={c.id}
                className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-line bg-canvas px-4 py-3.5"
              >
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-panel text-ink-soft">
                  <FileText className="h-5 w-5" strokeWidth={1.75} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate text-sm font-bold text-ink">{c.source}</span>
                    <Pill tone={status.tone}>{status.label}</Pill>
                  </div>
                  <div className="mt-1 truncate font-mono text-[10.5px] text-ink-faint">
                    {c.subjectRef} · {PERSONA_LABEL[c.subjectType]}
                  </div>
                  <div className="mt-1 grid gap-0.5 text-[11.5px] text-ink-soft">
                    <span>Otorgado: {formatDate(c.grantedAt, '—')}</span>
                    <span>Vence: {formatDate(c.expiresAt, 'Sin vencimiento')}</span>
                    {c.revokedAt && <span>Revocado: {formatDate(c.revokedAt, '—')}</span>}
                    {c.evidenceRef && (
                      <span className="font-mono text-[10.5px] text-ink-faint">
                        Evidencia: {c.evidenceRef}
                      </span>
                    )}
                  </div>
                </div>
                {active && (
                  <Button
                    variant="ghost"
                    className="shrink-0"
                    onClick={() => setRevokeTarget(c)}
                    disabled={revoke.isPending}
                  >
                    Revocar
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Registrar consentimiento */}
      <Dialog
        open={recordOpen}
        title="Registrar consentimiento"
        onClose={() => setRecordOpen(false)}
      >
        <div className="space-y-4">
          <div>
            <Label htmlFor="consent-subject">Identificación del sujeto (requerido)</Label>
            <Input
              id="consent-subject"
              value={form.subjectRef}
              onChange={(e) => setForm((f) => ({ ...f, subjectRef: e.target.value }))}
              placeholder="Cédula o RUC del cliente"
            />
          </div>
          <div>
            <Label htmlFor="consent-type">Tipo de sujeto</Label>
            <select
              id="consent-type"
              value={form.subjectType}
              onChange={(e) =>
                setForm((f) => ({ ...f, subjectType: e.target.value as Persona }))
              }
              className={CONTROL_CLS}
            >
              <option value="natural">{PERSONA_LABEL.natural}</option>
              <option value="legal">{PERSONA_LABEL.legal}</option>
            </select>
          </div>
          <div>
            <Label htmlFor="consent-source">Fuente / buró (requerido)</Label>
            <Input
              id="consent-source"
              value={form.source}
              onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}
              placeholder="APC"
            />
          </div>
          <div>
            <Label htmlFor="consent-evidence">Evidencia (opcional)</Label>
            <Input
              id="consent-evidence"
              value={form.evidenceRef}
              onChange={(e) => setForm((f) => ({ ...f, evidenceRef: e.target.value }))}
              placeholder="Referencia del documento firmado"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="consent-granted">Fecha de otorgamiento</Label>
              <input
                id="consent-granted"
                type="date"
                value={form.grantedAt}
                onChange={(e) => setForm((f) => ({ ...f, grantedAt: e.target.value }))}
                className={CONTROL_CLS}
              />
            </div>
            <div>
              <Label htmlFor="consent-expires">Vencimiento (opcional)</Label>
              <input
                id="consent-expires"
                type="date"
                value={form.expiresAt}
                onChange={(e) => setForm((f) => ({ ...f, expiresAt: e.target.value }))}
                className={CONTROL_CLS}
              />
            </div>
          </div>
          <p className="font-mono text-[10.5px] text-ink-faint">
            El consentimiento queda registrado en la bitácora inmutable.
          </p>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setRecordOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={submitRecord}
              disabled={
                create.isPending || !form.subjectRef.trim() || !form.source.trim()
              }
            >
              Registrar
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Revocar consentimiento (motivo requerido para la bitácora) */}
      {revokeTarget && (
        <ReasonModal
          open
          title="Revocar consentimiento"
          confirmLabel="Revocar"
          reasons={REVOKE_REASONS}
          tone="destructive"
          onClose={() => setRevokeTarget(null)}
          onConfirm={() => {
            if (revokeTarget) revoke.mutate(revokeTarget.id);
          }}
        />
      )}
    </Card>
  );
}
